import { msg } from "@lingui/core/macro"
import { Trans, useLingui } from "@lingui/react/macro"
import type {
  CompanyDeal,
  CompanyContact,
  CompanyDrawer as CompanyDrawerData,
  CompanyDrawerActivity,
} from "@/types/v2/company"
import { formatInteger } from "@/lib/number-formatting"
import {
  AvatarTile,
  CircleAvatar,
} from "@/components/v2/companies/avatar-tile"
import {
  DealStatusPill,
  LifecyclePill,
} from "@/components/v2/companies/pill"
import { SparkleIcon } from "@/components/v2/companies/icons"
import {
  pushDrawer,
  type DrawerNavContext,
} from "@/lib/drawer-trail"

interface CompanyEntryBodyProps {
  company: CompanyDrawerData
  // Drawer trail nav context so people-rows can push their
  // contact onto the trail (matching the People section
  // click affordance on the full company page).
  navContext: DrawerNavContext
}

// Body content for a company entry in the unified v2
// slideover. Extracted from the legacy `CompanyDrawer`.
export function CompanyEntryBody({
  company,
  navContext,
}: CompanyEntryBodyProps) {
  return (
    <div className="flex-1 overflow-y-auto px-6 pt-5 pb-8 flex flex-col gap-5">
      <Hero company={company} />
      <KpiRow company={company} />
      <DealsSection
        deals={company.deals}
        fullPageHref={company.url}
        navContext={navContext}
      />
      <PeopleSection
        contacts={company.contacts}
        totalCount={company.contactsCount}
        fullPageHref={company.url}
        navContext={navContext}
      />
      <ActivitySection
        activity={company.activity}
        fullPageHref={company.url}
      />
    </div>
  )
}

function Hero({ company }: { company: CompanyDrawerData }) {
  const meta = buildMeta(company)

  return (
    <div className="flex items-center gap-4">
      <AvatarTile
        initial={company.initial}
        color={company.initialColor}
        size={56}
        rounded="xl"
      />
      <div className="flex flex-col gap-1 grow min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="tracking-[-0.01em] font-semibold text-[#1E1B17] text-2xl leading-[30px] truncate">
            {company.name}
          </h2>
          {company.lifecycleLabel ? (
            <LifecyclePill label={company.lifecycleLabel} />
          ) : null}
        </div>
        {meta ? (
          <div className="text-[#7A746B] text-[13px] leading-4">
            {meta}
          </div>
        ) : null}
      </div>
    </div>
  )
}

function KpiRow({ company }: { company: CompanyDrawerData }) {
  const { t } = useLingui()
  return (
    <div className="grid grid-cols-3 gap-3">
      <KpiTile
        label={t`Open deals`}
        value={String(company.openDealsCount)}
        hint={
          company.openDealsCount > 0
            ? `${company.openDealsValueLabel} pipeline`
            : null
        }
      />
      <KpiTile
        label={t`Contacts`}
        value={String(company.contactsCount)}
        hint={
          company.contactsInActiveDealCount > 0
            ? `${company.contactsInActiveDealCount} in active deals`
            : null
        }
      />
      <KpiTile
        label={t`Last touch`}
        value={company.lastTouchLabel ?? "—"}
        hint={
          company.lastTouchLabel
            ? `via ${company.lastTouchChannel}`
            : null
        }
      />
    </div>
  )
}

function KpiTile({
  label,
  value,
  hint,
}: {
  label: string
  value: string
  hint: string | null
}) {
  return (
    <div className="flex flex-col gap-1 rounded-[10px] py-3 px-3.5 bg-[#FAF7F1] border border-[#EBE5D8]">
      <div className="tracking-[0.06em] uppercase font-semibold text-[#7A746B] text-[10px] leading-3">
        {label}
      </div>
      <div className="flex items-baseline gap-1.5">
        <div className="font-semibold text-[#1E1B17] text-xl leading-6">
          {value}
        </div>
        {hint ? (
          <div className="text-[#7A746B] text-[11px] leading-[14px]">
            {hint}
          </div>
        ) : null}
      </div>
    </div>
  )
}

function DealsSection({
  deals,
  fullPageHref,
  navContext,
}: {
  deals: CompanyDeal[]
  fullPageHref: string
  navContext: DrawerNavContext
}) {
  const { t } = useLingui()
  return (
    <DrawerSection
      title={dealsTitle(deals, t)}
      seeAllHref={deals.length > 0 ? fullPageHref : null}
    >
      {deals.length === 0 ? (
        <EmptyCard message={t`No deals yet at this company.`} />
      ) : (
        <div className="flex flex-col rounded-xl overflow-hidden bg-white border border-[#E5DFD3]">
          {deals.map((deal) => (
            <DealRow
              key={deal.id}
              deal={deal}
              navContext={navContext}
            />
          ))}
        </div>
      )}
    </DrawerSection>
  )
}

function dealsTitle(
  deals: CompanyDeal[],
  t: ReturnType<typeof useLingui>["t"],
): string {
  const open = deals.filter((d) => d.status === "open").length
  const won = deals.filter((d) => d.status === "won").length
  const parts: string[] = []
  if (open > 0) parts.push(t(msg`${open} open`))
  if (won > 0) parts.push(t(msg`${won} won`))
  if (parts.length === 0) return t(msg`Deals`)
  return t(msg`Deals · ${parts.join(" · ")}`)
}

function DealRow({
  deal,
  navContext,
}: {
  deal: CompanyDeal
  navContext: DrawerNavContext
}) {
  const isWon = deal.status === "won"
  const nameClass = isWon
    ? "text-[#7A746B]"
    : "text-[#1E1B17] font-semibold"

  return (
    <button
      type="button"
      onClick={() => pushDrawer("deal", deal.id, navContext)}
      data-testid={`company-drawer-deal-${deal.id}`}
      className="flex items-center py-3 px-3.5 gap-3 border-b border-[#F0EDE6] last:border-b-0 text-left w-full hover:bg-[#FAF7F1] cursor-pointer"
    >
      <DealStatusPill kind={deal.displayStatus} fixedWidth={false} />
      <div
        className={`grow basis-0 min-w-0 text-[13px] leading-4 truncate ${nameClass}`}
      >
        {deal.name}
      </div>
      <div className="text-[#7A746B] text-xs leading-4 shrink-0">
        {deal.stageName ?? "—"}
      </div>
      <div
        className={`font-semibold text-[13px] leading-4 shrink-0 tabular-nums ${
          isWon ? "text-[#5C8A4F]" : "text-[#1E1B17]"
        }`}
      >
        {deal.valueLabel ?? "—"}
      </div>
      <div className="text-[#9A938A] text-[11px] leading-[14px] shrink-0 tabular-nums">
        {deal.dateLabel}
      </div>
    </button>
  )
}

function PeopleSection({
  contacts,
  totalCount,
  fullPageHref,
  navContext,
}: {
  contacts: CompanyContact[]
  totalCount: number
  fullPageHref: string
  navContext: DrawerNavContext
}) {
  const { t } = useLingui()
  const title =
    totalCount > 0 ? t`People · ${totalCount}` : t`People`

  return (
    <DrawerSection
      title={title}
      seeAllHref={totalCount > contacts.length ? fullPageHref : null}
    >
      {contacts.length === 0 ? (
        <EmptyCard message={t`No contacts linked to this company yet.`} />
      ) : (
        <div className="flex flex-col rounded-xl overflow-hidden bg-white border border-[#E5DFD3]">
          {contacts.map((contact) => (
            <PersonRow
              key={contact.id}
              contact={contact}
              navContext={navContext}
            />
          ))}
        </div>
      )}
    </DrawerSection>
  )
}

function PersonRow({
  contact,
  navContext,
}: {
  contact: CompanyContact
  navContext: DrawerNavContext
}) {
  return (
    <button
      type="button"
      onClick={() => pushDrawer("contact", contact.id, navContext)}
      data-testid={`company-drawer-person-${contact.id}`}
      className="flex items-center py-2.5 px-3.5 gap-3 border-b border-[#F0EDE6] last:border-b-0 text-left w-full hover:bg-[#FAF7F1] cursor-pointer"
    >
      <CircleAvatar
        initials={contact.initials}
        color={contact.initialColor}
        size={32}
      />
      <div className="grow basis-0 min-w-0 flex flex-col gap-px">
        <div className="font-semibold text-[#1E1B17] text-[13px] leading-4 truncate">
          {contact.displayName}
        </div>
        {contact.jobTitle ? (
          <div className="text-[#7A746B] text-[11px] leading-[14px] truncate">
            {contact.jobTitle}
          </div>
        ) : null}
      </div>
      {contact.inActiveDeal ? (
        <span className="inline-flex items-center rounded-full py-0.5 px-1.75 gap-1 bg-[#FAF3EB] shrink-0">
          <SparkleIcon size={9} color="#B86A2D" />
          <span className="text-[#B86A2D] text-[10px] leading-3">
            <Trans>In active deal</Trans>
          </span>
        </span>
      ) : null}
    </button>
  )
}

function ActivitySection({
  activity,
  fullPageHref,
}: {
  activity: CompanyDrawerActivity[]
  fullPageHref: string
}) {
  const { t } = useLingui()
  return (
    <DrawerSection
      title={t`Recent activity`}
      seeAllHref={activity.length > 0 ? fullPageHref : null}
    >
      {activity.length === 0 ? (
        <EmptyCard message={t`No activity yet for this company.`} />
      ) : (
        <div className="flex flex-col gap-3">
          {activity.map((row) => (
            <ActivityRow key={row.id} row={row} />
          ))}
        </div>
      )}
    </DrawerSection>
  )
}

function ActivityRow({ row }: { row: CompanyDrawerActivity }) {
  const subtitleParts = [row.occurredAtLabel, row.dealName].filter(
    Boolean,
  )

  return (
    <div className="flex items-start gap-2.5">
      <div className="flex items-center justify-center shrink-0 mt-0.5 size-[22px] rounded-full bg-[#FAF3EB]">
        <SparkleIcon size={10} color="#B86A2D" />
      </div>
      <div className="grow basis-0 min-w-0 flex flex-col gap-0.5">
        <div className="flex items-baseline gap-1.5 flex-wrap">
          <span className="font-medium text-[#1E1B17] text-[13px] leading-4">
            {row.title}
          </span>
          {subtitleParts.length > 0 ? (
            <span className="text-[#9A938A] text-[11px] leading-[14px]">
              {subtitleParts.join(" · ")}
            </span>
          ) : null}
        </div>
        {row.body ? (
          <div className="text-[#5C544A] text-xs leading-4">
            {row.body}
          </div>
        ) : null}
      </div>
    </div>
  )
}

function DrawerSection({
  title,
  seeAllHref,
  children,
}: {
  title: string
  seeAllHref: string | null
  children: React.ReactNode
}) {
  return (
    <section className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between">
        <div className="tracking-[0.06em] uppercase font-semibold text-[#7A746B] text-[11px] leading-[14px]">
          {title}
        </div>
        {seeAllHref ? (
          <a
            href={seeAllHref}
            className="text-[#1E1B17] text-xs leading-4 hover:text-[#5C544A]"
          >
            <Trans>See all</Trans>
          </a>
        ) : null}
      </div>
      {children}
    </section>
  )
}

function EmptyCard({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-[#E5DFD3] bg-white px-4 py-6 text-center">
      <p className="text-[12px] leading-4 text-[#7A746B]">{message}</p>
    </div>
  )
}

function buildMeta(company: CompanyDrawerData): string | null {
  const parts: string[] = []
  if (company.domain) parts.push(company.domain)
  if (company.industry) parts.push(company.industry)
  if (company.employeeCount) {
    parts.push(`${formatInteger(company.employeeCount)} employees`)
  }
  if (parts.length === 0) return null
  return parts.join(" · ")
}
